import { AbstractSigner, Provider, Signer } from "ethers";
import { encodeFunctionData, isHex, size } from "viem";

import { getContract } from "config/contracts";
import { GMX_SIMULATION_ORIGIN } from "config/dataStore";
import { isSourceChain } from "config/multichain";
import type { BridgeOutParams } from "domain/multichain/types";
import {
  ExpressParamsEstimationMethod,
  ExpressTransactionBuilder,
  ExpressTransactionEstimatorParams,
  ExpressTxnParams,
  getGelatoRelayRouterDomain,
  GlobalExpressParams,
  hashRelayParams,
  RawRelayParamsPayload,
  RelayParamsPayload,
} from "domain/synthetics/express";
import type { Subaccount } from "domain/synthetics/subaccount";
import { getSubaccountValidations } from "domain/synthetics/subaccount/utils";
import { TokenData, TokensData } from "domain/tokens";
import { extendError } from "lib/errors";
import { metrics } from "lib/metrics";
import { getByKey } from "lib/objects";
import { createProviderRpc } from "lib/rpc/createProviderRpc";
import { ISigner } from "lib/transactions/iSigner";
import { WalletSigner } from "lib/wallets";
import { getSignatureKind, hashSignedTypedData, signTypedData, SignTypedDataParams } from "lib/wallets/signing";
import { getPublicClientWithRpc } from "lib/wallets/walletConfig";
import { abis } from "sdk/abis";
import { AnyChainId, ContractsChainId, SettlementChainId, SourceChainId } from "sdk/configs/chains";
import { ContractName } from "sdk/configs/contracts";
import { DEFAULT_EXPRESS_ORDER_DEADLINE_DURATION } from "sdk/configs/express";
import { bigMath } from "sdk/utils/bigmath";
import { type ExpressTxnData, estimateExpressParams as sdkEstimateExpressParams } from "sdk/utils/express";
import { getBatchTypedData, buildBatchOrderCalldata } from "sdk/utils/express";
import {
  BatchOrderTxnParams,
  getBatchExternalCalls,
  getBatchExternalSwapGasLimit,
  getBatchRequiredActions,
  getBatchTotalExecutionFee,
  getBatchTotalPayCollateralAmount,
  getIsEmptyBatch,
} from "sdk/utils/orderTransactions";
import type { IRpc, StateOverrideEntry } from "sdk/utils/rpc";
import { hashSubaccountApproval } from "sdk/utils/subaccount";
import { nowInSeconds } from "sdk/utils/time";

import { estimateBatchGasLimit, GasLimitsConfig } from "../fees";

export function getPrimaryOrderGasPaymentTokenAmount({
  expressParams,
  primaryExecutionFeeAmount,
}: {
  expressParams: Pick<ExpressTxnParams, "gasPaymentParams" | "executionFeeAmount">;
  primaryExecutionFeeAmount: bigint | undefined;
}): bigint {
  const { gasPaymentTokenAmount, relayerFeeAmount } = expressParams.gasPaymentParams;

  if (primaryExecutionFeeAmount === undefined) {
    return gasPaymentTokenAmount;
  }

  const batchFeeAmount = relayerFeeAmount + expressParams.executionFeeAmount;
  const primaryFeeAmount = relayerFeeAmount + primaryExecutionFeeAmount;

  if (batchFeeAmount <= 0n || primaryFeeAmount >= batchFeeAmount) {
    return gasPaymentTokenAmount;
  }

  return bigMath.mulDiv(gasPaymentTokenAmount, primaryFeeAmount, batchFeeAmount);
}

export async function estimateBatchExpressParams({
  signer,
  provider,
  chainId,
  batchParams,
  isGmxAccount,
  globalExpressParams,
  requireValidations,
  estimationMethod = "approximate",
  subaccount,
}: {
  chainId: ContractsChainId;
  isGmxAccount: boolean;
  signer: WalletSigner;
  provider: Provider;
  batchParams: BatchOrderTxnParams;
  globalExpressParams: GlobalExpressParams | undefined;
  estimationMethod: ExpressParamsEstimationMethod;
  requireValidations: boolean;
  subaccount: Subaccount | undefined;
}): Promise<ExpressTxnParams | undefined> {
  if (!globalExpressParams) {
    return undefined;
  }

  const transactionParams = getBatchExpressEstimatorParams({
    signer,
    batchParams,
    gasLimits: globalExpressParams.gasLimits,
    gasPaymentToken: globalExpressParams.gasPaymentToken,
    chainId,
    tokensData: globalExpressParams.tokensData,
    isGmxAccount,
    estimationMethod,
  });

  if (!transactionParams) {
    return undefined;
  }

  const expressParams = await estimateExpressParams({
    chainId,
    rpc: createProviderRpc(provider),
    transactionParams,
    globalExpressParams,
    estimationMethod,
    requireValidations,
    isGmxAccount,
    subaccount,
  });

  return expressParams;
}

function getBatchExpressEstimatorParams({
  signer,
  batchParams,
  gasLimits,
  gasPaymentToken,
  chainId,
  tokensData,
  isGmxAccount,
  estimationMethod,
}: {
  signer: WalletSigner;
  batchParams: BatchOrderTxnParams;
  gasLimits: GasLimitsConfig;
  gasPaymentToken: TokenData;
  isGmxAccount: boolean;
  chainId: ContractsChainId;
  tokensData: TokensData;
  estimationMethod?: ExpressParamsEstimationMethod;
}): ExpressTransactionEstimatorParams | undefined {
  const payAmounts = getBatchTotalPayCollateralAmount(batchParams);
  const gasPaymentTokenAsCollateralAmount = getByKey(payAmounts, gasPaymentToken.address) ?? 0n;
  const executionFeeAmount = getBatchTotalExecutionFee({
    batchParams,
    chainId,
    tokensData,
    allowEmptyBatch: estimationMethod === "approximate",
  });
  const transactionExternalCalls = getBatchExternalCalls(batchParams);
  const subaccountActions = getBatchRequiredActions(batchParams);
  const transactionPayloadGasLimit = estimateBatchGasLimit({
    gasLimits,
    createOrdersCount: batchParams.createOrderParams.length,
    updateOrdersCount: batchParams.updateOrderParams.length,
    cancelOrdersCount: batchParams.cancelOrderParams.length,
    externalCallsGasLimit: getBatchExternalSwapGasLimit(batchParams),
    isGmxAccount,
  });

  if (!executionFeeAmount) {
    return undefined;
  }

  const expressTransactionBuilder: ExpressTransactionBuilder = async ({
    relayParams,
    gasPaymentParams,
    subaccount,
  }) => {
    return {
      txnData: await buildAndSignExpressBatchOrderTxn({
        chainId,
        batchParams,
        relayParamsPayload: relayParams,
        relayerFeeTokenAddress: gasPaymentParams.relayerFeeTokenAddress,
        relayerFeeAmount: gasPaymentParams.relayerFeeAmount,
        subaccount,
        signer,
        emptySignature: true,
        isGmxAccount,
      }),
    };
  };

  return {
    account: signer.address,
    gasPaymentTokenAsCollateralAmount,
    executionFeeAmount: executionFeeAmount.feeTokenAmount,
    executionGasLimit: executionFeeAmount.gasLimit,
    transactionPayloadGasLimit,
    transactionExternalCalls,
    subaccountActions,
    isValid: !getIsEmptyBatch(batchParams),
    expressTransactionBuilder,
  };
}

export { ExpressEstimationError, ExpressEstimationInsufficientGasPaymentTokenBalanceError } from "sdk/utils/express";

export async function estimateExpressParams({
  chainId,
  isGmxAccount,
  transactionParams,
  globalExpressParams,
  estimationMethod = "approximate",
  requireValidations = true,
  subaccount,
  throwOnInvalid = false,
  rpc,
  stateOverride,
  overrideGasLimit,
}: {
  chainId: ContractsChainId;
  isGmxAccount: boolean;
  globalExpressParams: GlobalExpressParams;
  transactionParams: ExpressTransactionEstimatorParams;
  overrideGasLimit?: bigint;
  estimationMethod: ExpressParamsEstimationMethod;
  requireValidations: boolean;
  subaccount: Subaccount | undefined;
  throwOnInvalid?: boolean;
  rpc?: IRpc;
  stateOverride?: StateOverrideEntry[];
}): Promise<ExpressTxnParams | undefined> {
  return sdkEstimateExpressParams({
    chainId,
    isGmxAccount,
    transactionParams,
    globalExpressParams,
    estimationMethod,
    requireValidations,
    subaccount,
    throwOnInvalid,
    overrideGasLimit,
    rpc,
    simulationOrigin: GMX_SIMULATION_ORIGIN,
    stateOverride,
    gasPaymentAllowanceData: globalExpressParams.gasPaymentAllowanceData,
    getSubaccountValidations,
    metrics,
  });
}

export {
  getGasPaymentValidations,
  getIsConfirmedOutOfGasPaymentTokenBalance,
  getIsValidExpressParams,
} from "sdk/utils/express";

export async function buildAndSignExpressBatchOrderTxn({
  chainId,
  batchParams,
  relayParamsPayload,
  relayerFeeTokenAddress,
  relayerFeeAmount,
  subaccount,
  signer,
  isGmxAccount,
  emptySignature = false,
}: {
  signer: WalletSigner;
  chainId: ContractsChainId;
  batchParams: BatchOrderTxnParams;
  relayerFeeTokenAddress: string;
  relayerFeeAmount: bigint;
  relayParamsPayload: RawRelayParamsPayload;
  isGmxAccount: boolean;
  subaccount: Subaccount | undefined;
  emptySignature?: boolean;
}): Promise<ExpressTxnData> {
  const messageSigner = subaccount ? subaccount!.signer : signer;
  const crossChainId = isGmxAccount ? await getMultichainInfoFromSigner(signer, chainId) : undefined;
  const effectiveSrcChainId = isGmxAccount ? crossChainId ?? chainId : undefined;
  const relayRouterAddress = getOrderRelayRouterAddress(chainId, subaccount !== undefined, isGmxAccount);

  const relayPayload: RelayParamsPayload = {
    ...(relayParamsPayload as RelayParamsPayload),
    deadline: BigInt(nowInSeconds() + DEFAULT_EXPRESS_ORDER_DEADLINE_DURATION),
    userNonce: BigInt(nowInSeconds()),
  };

  let signature: string;
  if (emptySignature) {
    signature = "0x";
  } else {
    const typedData = getBatchTypedData({
      chainId,
      signingChainId: crossChainId ?? chainId,
      batchParams,
      relayParams: relayPayload,
      account: signer.address,
      subaccountApprovalHash: subaccount?.signedApproval
        ? hashSubaccountApproval(subaccount.signedApproval)
        : undefined,
      relayRouterAddress,
    });

    const signatureParams: SignTypedDataParams = {
      signer: messageSigner,
      types: typedData.types,
      typedData: typedData.message,
      domain: typedData.domain,
      shouldUseSignerMethod: subaccount !== undefined,
      verificationChainId: chainId,
    };

    signature = await signTypedData(signatureParams);

    validateSignature({
      signatureParams,
      signature,
      expectedAccount: messageSigner.address,
      errorSource: "expressOrders.batch.signatureValidation",
      silent: true,
    });
  }

  const result = buildBatchOrderCalldata({
    chainId,
    batchParams,
    relayParamsPayload: relayPayload,
    signature,
    account: signer.address,
    srcChainId: effectiveSrcChainId as any,
    subaccountApproval: subaccount?.signedApproval,
    isGmxAccount,
    deadline: relayPayload.deadline,
  });

  return {
    callData: result.callData,
    to: result.to,
    feeToken: relayerFeeTokenAddress,
    feeAmount: relayerFeeAmount,
  };
}

export async function getMultichainInfoFromSigner(
  signer: Signer,
  chainId: ContractsChainId
): Promise<SourceChainId | undefined> {
  const srcChainId = await signer.provider!.getNetwork().then((n) => Number(n.chainId) as AnyChainId);

  if (!isSourceChain(srcChainId, chainId)) {
    return undefined;
  }

  const isMultichain = srcChainId !== (chainId as SourceChainId);

  if (!isMultichain) {
    return undefined;
  }

  return srcChainId;
}

export function getOrderRelayRouterAddress(
  chainId: ContractsChainId,
  isSubaccount: boolean,
  isMultichain: boolean
): string {
  let contractName: ContractName;
  if (isMultichain) {
    if (isSubaccount) {
      contractName = "MultichainSubaccountRouter";
    } else {
      contractName = "MultichainOrderRouter";
    }
  } else {
    if (isSubaccount) {
      contractName = "SubaccountGelatoRelayRouter";
    } else {
      contractName = "GelatoRelayRouter";
    }
  }

  return getContract(chainId, contractName);
}

// TODO MLTCH: move to bridge out utils
export async function buildAndSignBridgeOutTxn({
  chainId,
  srcChainId,
  relayParamsPayload,
  params,
  signer,
  account,
  emptySignature = false,
  relayerFeeTokenAddress,
  relayerFeeAmount,
}: {
  chainId: SettlementChainId;
  srcChainId: SourceChainId;
  relayParamsPayload: RawRelayParamsPayload;
  params: BridgeOutParams;
  signer: WalletSigner | ISigner | undefined;
  account: string;
  emptySignature?: boolean;
  relayerFeeTokenAddress: string;
  relayerFeeAmount: bigint;
}): Promise<ExpressTxnData> {
  let signature: string;

  const relayParams: RelayParamsPayload = {
    ...relayParamsPayload,
    deadline: BigInt(nowInSeconds() + DEFAULT_EXPRESS_ORDER_DEADLINE_DURATION),
  };

  if (emptySignature) {
    signature = "0x";
  } else {
    if (!signer) {
      throw new Error("Signer is required");
    }

    signature = await signBridgeOutPayload({
      relayParams,
      params,
      signer,
      chainId,
      srcChainId,
    });
  }

  const bridgeOutCallData = encodeFunctionData({
    abi: abis.MultichainTransferRouter,
    functionName: "bridgeOut",
    args: [
      {
        ...relayParams,
        signature,
      },
      account,
      BigInt(srcChainId),
      params,
    ],
  });

  return {
    callData: bridgeOutCallData,
    to: getContract(chainId, "MultichainTransferRouter"),
    feeToken: relayerFeeTokenAddress,
    feeAmount: relayerFeeAmount,
  };
}

async function signBridgeOutPayload({
  signer,
  relayParams,
  params,
  chainId,
  srcChainId,
}: {
  signer: WalletSigner | ISigner;
  relayParams: RelayParamsPayload;
  params: BridgeOutParams;
  chainId: SettlementChainId;
  srcChainId: SourceChainId;
}): Promise<string> {
  const types = {
    BridgeOut: [
      { name: "token", type: "address" },
      { name: "amount", type: "uint256" },
      { name: "minAmountOut", type: "uint256" },
      { name: "provider", type: "address" },
      { name: "data", type: "bytes" },
      { name: "relayParams", type: "bytes32" },
    ],
  };

  const typedData = {
    token: params.token,
    amount: params.amount,
    minAmountOut: params.minAmountOut,
    provider: params.provider,
    data: params.data,
    relayParams: hashRelayParams(relayParams),
  };

  const domain = getGelatoRelayRouterDomain(srcChainId, getContract(chainId, "MultichainTransferRouter"));

  return signTypedData({ signer, domain, types, typedData, verificationChainId: chainId });
}

export async function signSetTraderReferralCode({
  signer,
  relayParams,
  referralCode,
  chainId,
  srcChainId,
  shouldUseSignerMethod,
}: {
  signer: AbstractSigner | ISigner;
  relayParams: RelayParamsPayload;
  referralCode: string;
  chainId: ContractsChainId;
  srcChainId: SourceChainId;
  shouldUseSignerMethod?: boolean;
}) {
  const types = {
    SetTraderReferralCode: [
      { name: "referralCode", type: "bytes32" },
      { name: "relayParams", type: "bytes32" },
    ],
  };

  const domain = getGelatoRelayRouterDomain(srcChainId ?? chainId, getContract(chainId, "MultichainOrderRouter"));
  const typedData = {
    referralCode: referralCode,
    relayParams: hashRelayParams(relayParams),
  };

  return signTypedData({ signer, domain, types, typedData, shouldUseSignerMethod, verificationChainId: chainId });
}

export async function signRegisterCode({
  signer,
  relayParams,
  referralCode,
  chainId,
  srcChainId,
  shouldUseSignerMethod,
}: {
  signer: AbstractSigner | ISigner;
  relayParams: RelayParamsPayload;
  referralCode: string;
  chainId: ContractsChainId;
  srcChainId: SourceChainId;
  shouldUseSignerMethod?: boolean;
}) {
  const types = {
    RegisterCode: [
      { name: "referralCode", type: "bytes32" },
      { name: "relayParams", type: "bytes32" },
    ],
  };

  const domain = getGelatoRelayRouterDomain(srcChainId ?? chainId, getContract(chainId, "MultichainOrderRouter"));
  const typedData = {
    referralCode: referralCode,
    relayParams: hashRelayParams(relayParams),
  };

  return signTypedData({ signer, domain, types, typedData, shouldUseSignerMethod, verificationChainId: chainId });
}

export const SIGNATURE_VALIDATION_FAILED_ERROR = "Signature validation failed";
export const SIGNATURE_VALIDATION_UNAVAILABLE_ERROR = "Signature validation unavailable";

async function validateSignature({
  signatureParams,
  signature,
  expectedAccount,
  silent = false,
  errorSource = "validateSignature",
}: {
  signatureParams: Pick<SignTypedDataParams, "domain" | "types" | "typedData" | "verificationChainId">;
  signature: string;
  expectedAccount: string;
  silent?: boolean;
  errorSource?: string;
}) {
  const { verificationChainId } = signatureParams;
  let signedHash: string | undefined;

  try {
    if (!isHex(signature)) {
      throw new Error("Signature is not a hex string");
    }

    signedHash = hashSignedTypedData(signatureParams);

    // Covers EOA, ERC-1271 and ERC-6492, matching what the relay router accepts.
    const isValid = await getPublicClientWithRpc(verificationChainId).verifyHash({
      address: expectedAccount,
      hash: signedHash,
      signature,
    });

    if (!isValid) {
      throw new Error(SIGNATURE_VALIDATION_FAILED_ERROR);
    }
  } catch (error) {
    const isRejected = error?.message === SIGNATURE_VALIDATION_FAILED_ERROR;
    // No raw signature in telemetry — it authorizes the order, and viem appends it to the cause.
    const diagnostics = {
      signedHash,
      verificationChainId,
      signingChainId: signatureParams.domain.chainId,
      expectedAccount,
      signatureKind: getSignatureKind(signature),
      signatureBytes: isHex(signature) ? size(signature) : undefined,
    };
    const extended = extendError(isRejected ? error : new Error(SIGNATURE_VALIDATION_UNAVAILABLE_ERROR), {
      errorSource,
      data: isRejected ? diagnostics : { ...diagnostics, cause: error?.message?.split("\n")[0] },
    });

    metrics.pushError(extended, errorSource);

    if (silent) {
      return;
    }

    throw extended;
  }
}
